import { useEffect, useState } from 'react';
import AvatarStack from '../Common/AvatarStack';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';

/**
 * SessionFocusIndicator Component
 * 
 * Displays a visual indicator (avatar stack) showing which proctors are currently 
 * focused on a specific session. This component monitors the userInteractions 
 * object in the session store to track real-time focus state changes.
 * 
 * **Key Features:**
 * - Displays avatars of all proctors currently focused on the session
 * - Shows talking indicators when proctors have their microphone active
 * - Filters out the current user (you don't see your own avatar)
 * - Only shows proctors who are currently online (connected to the room)
 * - Auto-updates when focus state changes via the sync document
 * 
 * **Data Flow:**
 * 1. SessionViewer updates userInteractions in sync document when focus changes
 * 2. ExamSessionStore triggers onUserInteraction event
 * 3. Session store updates userInteractions object
 * 4. This component re-renders with updated focused users list
 * 
 * @component
 * @param {Object} props
 * @param {string} props.sessionSid - The session SID to monitor for focused users
 * @param {string} [props.talkingIndicatorPosition='bottom'] - Position of talking indicator ('top', 'bottom', 'left', 'right')
 * @param {boolean} [props.showTooltip=true] - Whether to show tooltips on hover with user names
 * 
 * @example
 * // Basic usage on a session card
 * <SessionFocusIndicator sessionSid="ES123abc" />
 * 
 * @example
 * // With custom talking indicator position
 * <SessionFocusIndicator 
 *   sessionSid="ES123abc"
 *   talkingIndicatorPosition="top"
 *   showTooltip={true}
 * />
 */
const SessionFocusIndicator = ({
    sessionSid,
    talkingIndicatorPosition = 'bottom',
    showTooltip = true
}) => {

    // Local state to store the list of focused users for rendering
    const [focusedUsers, setFocusedUsers] = useState([]);

    // Subscribe to user interactions for this specific session
    // userInteractions structure: { [sessionSid]: { [userKeySid]: { isFocused, firstName, lastName, ... } } }
    const userInteractions = useSessionStore((state) => state.userInteractions?.[sessionSid]);

    // Get the list of currently online proctors to filter out offline users
    const proctors = useAppStore((state) => state.proctors);

    // Get current user to exclude them from the focused users list
    const user = useAppStore((state) => state.user);

    // Get the session to check reviewBy field
    const session = useSessionStore((state) => state.sessions?.[sessionSid]);

    /**
     * Effect: Process user interactions and build focused users list
     * 
     * This effect runs whenever userInteractions changes for this session.
     * It filters the interactions to find proctors who are:
     * 1. Currently focused (isFocused === true)
     * 2. Not the current user (no need to show your own avatar)
     * 3. Currently online (exists in the proctors object)
     */
    useEffect(() => {
        if (userInteractions) {
            const focusedUsers = [];

            // Iterate through all user interactions for this session
            for (const proctorSid in userInteractions) {
                // Three-part validation:
                // 1. isFocused must be true (proctor is actively focused on this session)
                // 2. Not the current user (proctorSid !== user.sid)
                // 3. Proctor is online (exists in proctors object from app store)
                if (userInteractions[proctorSid]?.isFocused
                    && proctorSid !== user.sid
                    && proctors[proctorSid]) {

                    const proctor = userInteractions[proctorSid];

                    // Check if this proctor is the reviewer by checking session.reviewBy
                    const isPrimary = session?.sessionData?.reviewBy === proctorSid;

                    // Build user object for AvatarStack component
                    const focusedUser = {
                        name: `${proctor.firstName} ${proctor.lastName}`,
                        src: proctor.avatar,                    // Avatar image URL
                        isTalking: proctor.isTalking || false,           // Microphone active state
                        isPrimary: isPrimary || false,
                        focusedSince: proctor.isFocusedChangeAt // Timestamp when focus started
                    };

                    focusedUsers.push(focusedUser);
                }
            }

            // Update local state with the filtered list
            setFocusedUsers(focusedUsers);
        }
    }, [userInteractions, proctors, user.sid, sessionSid, session?.reviewBy]); // Re-run when userInteractions or reviewBy changes

    // Don't render anything if no users are focused on this session
    if (!focusedUsers || focusedUsers.length === 0) {
        return null;
    }

    // Render the avatar stack with focused users
    return (
        <AvatarStack
            talkingIndicatorPosition={talkingIndicatorPosition}
            showTooltip={showTooltip}
            users={focusedUsers}
            color="ash"              // Gray color scheme
            hasInverseColor={true}   // Use inverse colors for better contrast
            primaryUserColor="fire"
            primaryUserInverseColor={true}
        />
    );
};

export default SessionFocusIndicator;
